/* ============================================================
   Gutful PDP V3 — theme interactions (gf-v3 namespace)
   Ported from the proven V1 gutful-pdp.js contracts (ATC, fly-to-cart,
   Horizon drawer adapter, before/after slider, section lifecycle) and
   extended for V3: variant-bound buy-box state, subscription/one-time
   gift toggling, disclosure + FAQ accordions, study modal/sheet, mobile
   sticky CTA, and a consent-aware analytics wrapper.

   Loaded (defer) by every gutful-v3-* section that needs JS. Init is
   guarded/idempotent and re-runs on shopify:section:load so the theme
   editor stays live without duplicate listeners, nodes, or events.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var gfShopRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  function gfRoute(path) { return gfShopRoot + String(path || '').replace(/^\/+/, ''); }

  function gfNumericId(value) {
    var match = String(value || '').match(/(\d+)$/);
    return match ? Number(match[1]) : 0;
  }

  /*
   * Shopify share-preview hosts do not reliably resolve cross-product
   * /products/{handle}.js requests in the context of the product being
   * previewed. Preserve the release-audited IDs on those hosts instead of
   * letting a preview-only response mark a real offer variant as sold out.
   * Shopify's cart endpoint remains the final inventory and plan gate.
   */
  function gfUseConfiguredCommerceOnPreview(locationLike) {
    var hostname = String(locationLike && locationLike.hostname || '').toLowerCase();
    return hostname === 'shopifypreview.com' || /\.shopifypreview\.com$/.test(hostname);
  }

  /*
   * Resolve commerce state from Shopify's public product JSON. This endpoint
   * is authoritative for the product itself and does not inherit the
   * cross-product availability context of the Start Here Liquid template.
   */
  function gfResolveProductCommerce(productData, variantId, sellingPlanId) {
    var variants = productData && Array.isArray(productData.variants) ? productData.variants : [];
    var wantedVariantId = gfNumericId(variantId);
    var wantedPlanId = gfNumericId(sellingPlanId);
    var variant = null;
    var allocation = null;

    variants.some(function (candidate) {
      if (gfNumericId(candidate && candidate.id) !== wantedVariantId) return false;
      variant = candidate;
      return true;
    });

    if (variant && wantedPlanId > 0) {
      var allocations = Array.isArray(variant.selling_plan_allocations)
        ? variant.selling_plan_allocations
        : [];
      allocations.some(function (candidate) {
        var candidatePlanId = gfNumericId(
          candidate && (
            candidate.selling_plan_id ||
            (candidate.selling_plan && candidate.selling_plan.id)
          )
        );
        if (candidatePlanId !== wantedPlanId) return false;
        allocation = candidate;
        return true;
      });
    }

    return {
      variantFound: Boolean(variant),
      available: Boolean(variant && variant.available === true),
      sellingPlanAvailable: Boolean(
        variant &&
        variant.available === true &&
        wantedPlanId > 0 &&
        allocation
      ),
      sellingPlanId: allocation ? wantedPlanId : 0
    };
  }

  window.GutfulCommerce = window.GutfulCommerce || {};
  window.GutfulCommerce.resolveProductState = gfResolveProductCommerce;
  window.GutfulCommerce.useConfiguredStateOnPreview = gfUseConfiguredCommerceOnPreview;

  /* ---------------------------------------------------------------
     Analytics: one consent-aware wrapper. Mirrors to Shopify analytics and
     dataLayer (GA4). Never sends PII/health.
     --------------------------------------------------------------- */
  function gfTrack(event, params) {
    try {
      var payload = params || {};
      var analyticsAllowed = false;
      try {
        analyticsAllowed = Boolean(window.Shopify && Shopify.customerPrivacy && Shopify.customerPrivacy.analyticsProcessingAllowed());
      } catch (privacyError) { /* fail closed for third-party analytics */ }
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(event, payload);
      }
      if (analyticsAllowed && Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: event }, payload));
      }
    } catch (e) { /* analytics must never break the page */ }
  }

  function money(el) { return el ? (el.textContent || '').trim() : ''; }

  /* ---------------------------------------------------------------
     Gallery — thumbnail → main image. Preserves object-fit per thumb.
     Changing gallery media must NOT reset the selected tier/mode.
     --------------------------------------------------------------- */
  function initGallery(root) {
    var track = root.querySelector('[data-gf-gallery-track]');
    var thumbs = root.querySelectorAll('.gf-hero__thumb');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-gf-slide]'));
    if (!slides.length) return;
    var prevBtn = root.querySelector('[data-gf-gallery-prev]');
    var nextBtn = root.querySelector('[data-gf-gallery-next]');

    function currentIndex() {
      var mid = track.scrollLeft + track.clientWidth / 2;
      var best = 0, bestDist = Infinity;
      slides.forEach(function (s, i) {
        var c = s.offsetLeft + s.offsetWidth / 2;
        var d = Math.abs(c - mid);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    }

    function goTo(i, smooth) {
      var s = slides[Math.max(0, Math.min(slides.length - 1, i))];
      if (!s) return;
      track.scrollTo({ left: s.offsetLeft, behavior: smooth === false || reduceMotion ? 'auto' : 'smooth' });
    }

    // Centre the active thumb in its strip. scrollIntoView('nearest') only
    // moves the minimum distance, so the last thumb parked at the edge
    // instead of centring; this also never scrolls the page.
    var strip = root.querySelector('.gf-hero__thumbs');
    function centerThumb(t) {
      if (!strip || !t) return;
      var target = t.offsetLeft - (strip.clientWidth - t.offsetWidth) / 2;
      var max = strip.scrollWidth - strip.clientWidth;
      strip.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    // Native scroll drives the active state — swipe, arrow, or thumb all
    // end up here, so there is one source of truth.
    function syncActive() {
      var i = currentIndex();
      thumbs.forEach(function (t, ti) {
        var on = ti === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) centerThumb(t);
      });
      if (prevBtn) prevBtn.disabled = i === 0;
      if (nextBtn) nextBtn.disabled = i === slides.length - 1;
    }

    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = null; syncActive(); });
    }, { passive: true });

    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.preventDefault(); goTo(currentIndex() - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); goTo(currentIndex() + 1); });

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { goTo(i); });
    });

    // Expose for the buy box (tier click scrolls to that variant's slide).
    root.__gfGalleryGoToSrc = function (src) {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].dataset.image === src) { goTo(i); return true; }
      }
      return false;
    };
    root.__gfGalleryCurrentImage = function () {
      var slide = slides[currentIndex()];
      var image = slide && slide.querySelector('img');
      return (image && (image.currentSrc || image.src)) || (slide && slide.dataset.image) || '';
    };
    syncActive();
  }

  /* ---------------------------------------------------------------
     Buy box — one shared state model. Supply tier, variant, purchase
     mode, price, selling plan, and gift visibility stay in sync; the
     primary CTA and sticky CTA both consume this state.
  --------------------------------------------------------------- */
  function initBuyBox(root) {
    var options = root.querySelectorAll('[data-gf-supply-option], .gf-supply-option');
    var cta = root.querySelector('[data-gf-atc]');
    if (!options.length || !cta) return;

    var ctaPrice = root.querySelector('[data-gf-cta-price]');
    var ctaCompare = root.querySelector('[data-gf-cta-compare]');
    var onetimePrice = root.querySelector('[data-gf-onetime-price]');
    var mainImage = root.querySelector('[data-gf-gallery-main]');
    var thumbs = root.querySelectorAll('.gf-hero__thumb');
    var refillToggle = root.querySelector('[data-gf-refill-toggle]');
    var refillCard = root.querySelector('[data-gf-refill-card]');
    var tierTray = root.querySelector('[data-gf-tier-value-tray]');
    var tierWraps = root.querySelectorAll('[data-gf-tier-wrap]');
    var stock = root.querySelector('[data-gf-stock], .gf-rb__stock');
    var stockTextNode = null;
    var stockAvailableWrap = stock && stock.querySelector('[data-gf-stock-available-wrap]');
    var stockUnavailableWrap = stock && stock.querySelector('[data-gf-stock-unavailable-wrap]');
    var stockCopyNode = stock && stock.querySelector('[data-gf-stock-copy]');
    var stockArrivalDateNode = stock && stock.querySelector('[data-gf-arrival-date]');
    var isRosabella = Boolean(
      (root.matches && root.matches('.gf-rb')) ||
      (root.closest && root.closest('.gf-rb'))
    );
    var offerId = isRosabella
      ? String(root.dataset.gfOfferId || 'gutful-rosabella-v1')
      : '';
    var postCheckoutBogo = isRosabella &&
      String(root.dataset.gfPostCheckoutBogo || '') === 'true';
    var mode = root.dataset.gfMode === 'onetime' ? 'onetime' : 'subscription';
    var previewCartSessionPromise = null;

    /*
     * Shopify's share-preview host can render a cached PDP while its cart
     * session cookie is missing or stale. In that state /cart/add.js returns
     * a false 422 "sold out" response for every cross-product offer variant.
     * A credentialed, uncached PDP GET initializes the preview cart context.
     * Keep this preview-only so the live storefront does not pay for an extra
     * document request.
     */
    function ensurePreviewCartSession() {
      if (!isRosabella || !gfUseConfiguredCommerceOnPreview(window.location)) {
        return Promise.resolve();
      }
      if (previewCartSessionPromise) return previewCartSessionPromise;
      var separator = window.location.search ? '&' : '?';
      var warmUrl = window.location.pathname + window.location.search +
        separator + '_gf_preview_cart_session=' + Date.now();
      previewCartSessionPromise = fetch(warmUrl, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('preview cart session unavailable');
        })
        .catch(function (error) {
          // A later click must be allowed to retry a transient warm-up failure.
          previewCartSessionPromise = null;
          throw error;
        });
      return previewCartSessionPromise;
    }

    if (stock && !stockAvailableWrap) {
      Array.prototype.some.call(stock.childNodes, function (node) {
        if (node.nodeType !== 3 || !(node.textContent || '').trim()) return false;
        stockTextNode = node;
        return true;
      });
    }
    var stockAvailableText = root.dataset.gfStockAvailable || (stockCopyNode && stockCopyNode.textContent.trim()) || (stockTextNode && stockTextNode.textContent.trim()) || 'In stock';
    if (/^currently unavailable$/i.test(stockAvailableText)) stockAvailableText = 'In stock';
    if (stockCopyNode) stockCopyNode.textContent = stockAvailableText;
    if (stockArrivalDateNode) {
      var configuredBusinessDays = Number(root.dataset.gfDeliveryBusinessDays);
      var deliveryBusinessDays = Number.isFinite(configuredBusinessDays) && configuredBusinessDays >= 0
        ? Math.round(configuredBusinessDays)
        : 3;
      stockArrivalDateNode.textContent = formatArrivalDate(addBusinessDays(new Date(), deliveryBusinessDays));
    }

    function selected() {
      return root.querySelector('[data-gf-supply-option].is-selected, .gf-supply-option.is-selected') || options[0];
    }

    function currentPrice(opt) {
      return mode === 'subscription' ? opt.dataset.priceSub : opt.dataset.priceOnetime;
    }

    function availabilityError(opt) {
      if (!opt || !opt.dataset.variantId) return 'This supply option is not configured.';
      if (opt.dataset.variantAvailable !== 'true') return 'This supply option is sold out.';
      if (mode === 'subscription' && (opt.dataset.subscriptionAvailable !== 'true' || !opt.dataset.sellingPlan)) {
        return 'Subscription is not configured for this supply option.';
      }
      return '';
    }

    function dataCents(opt, key) {
      if (!opt || typeof opt.dataset[key] === 'undefined' || opt.dataset[key] === '') return null;
      var value = Number(opt.dataset[key]);
      return isFinite(value) ? value : null;
    }

    function seedConfiguredCommerce(opt) {
      if (!opt) return;
      var variantId = gfNumericId(opt.dataset.variantId);
      var productHandle = String(opt.dataset.productHandle || '').trim();
      var oneTimePriceCents = dataCents(opt, 'priceOnetimeCents');
      var configuredPlanId = gfNumericId(opt.dataset.configuredSellingPlan);
      var subscriptionPriceCents = dataCents(opt, 'priceSubCents');

      /*
       * The IDs and fallback prices are release-audited against Shopify Admin.
       * Use them immediately so a context-false ProductDrop cannot disable the
       * offer before the product-level JSON check completes.
       */
      if (variantId > 0 && productHandle && oneTimePriceCents > 0) {
        opt.dataset.variantAvailable = 'true';
        opt.dataset.gfCommerceState = 'configured';
      }
      if (
        opt.dataset.variantAvailable === 'true' &&
        configuredPlanId > 0 &&
        subscriptionPriceCents > 0
      ) {
        opt.dataset.sellingPlan = String(configuredPlanId);
        opt.dataset.subscriptionAvailable = 'true';
      }
    }

    function hydrateCommerceOption(opt) {
      var productHandle = String(opt.dataset.productHandle || '').trim();
      var variantId = gfNumericId(opt.dataset.variantId);
      var configuredPlanId = gfNumericId(opt.dataset.configuredSellingPlan);
      if (!productHandle || variantId <= 0) return Promise.resolve();

      if (gfUseConfiguredCommerceOnPreview(window.location)) {
        opt.dataset.gfCommerceState = 'configured-preview';
        return Promise.resolve();
      }

      return fetch(gfRoute('products/' + encodeURIComponent(productHandle) + '.js'), {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('product commerce unavailable');
          return response.json();
        })
        .then(function (productData) {
          /*
           * A parseable but incomplete edge/CDN response is not evidence that a
           * release-audited variant disappeared. Preserve the configured state
           * and let Shopify's cart endpoint remain the final inventory gate.
           */
          if (
            !productData ||
            !Array.isArray(productData.variants) ||
            productData.variants.length === 0
          ) {
            throw new Error('indeterminate product commerce');
          }
          var state = gfResolveProductCommerce(productData, variantId, configuredPlanId);
          if (!state.variantFound) {
            throw new Error('indeterminate product variant');
          }
          opt.dataset.variantAvailable = state.available ? 'true' : 'false';
          opt.dataset.subscriptionAvailable = state.sellingPlanAvailable ? 'true' : 'false';
          opt.dataset.sellingPlan = state.sellingPlanAvailable ? String(state.sellingPlanId) : '';
          opt.dataset.gfCommerceState = state.variantFound ? 'verified' : 'variant-missing';
          /*
           * Do not wait for the other tiers before reflecting the selected
           * tier's authoritative result. This closes the stale UI/CTA race.
           */
          if (opt === selected()) render();
        })
        .catch(function () {
          /*
           * Keep the audited configured state on a transient JSON/network
           * failure. Shopify's cart endpoint remains the final inventory gate.
           */
          opt.dataset.gfCommerceState = 'configured-fallback';
        });
    }

    function activePriceCents(opt) {
      return dataCents(opt, mode === 'subscription' ? 'priceSubCents' : 'priceOnetimeCents');
    }

    function shouldShowCompare(opt, compareText) {
      var compareCents = dataCents(opt, 'compareTotalCents');
      var priceCents = activePriceCents(opt);
      if (compareCents !== null && priceCents !== null) return compareCents > priceCents;
      return Boolean((compareText || '').trim());
    }

    function syncComparePrices(opt) {
      options.forEach(function (option) {
        var strike = option.querySelector('[data-gf-tier-compare], .gf-rb-sr__tier-prices s, .gf-supply-option__strike');
        if (!strike) return;
        var compareText = option.dataset.compareTotal || strike.textContent || '';
        strike.hidden = !shouldShowCompare(option, compareText);
      });
      if (!ctaCompare) return;
      var ctaCompareText = opt.dataset.compareTotal || '';
      ctaCompare.textContent = ctaCompareText;
      ctaCompare.hidden = !shouldShowCompare(opt, ctaCompareText);
    }

    function syncStock(opt) {
      if (!stock) return;
      var available = opt && opt.dataset.variantAvailable === 'true';
      var label = available ? stockAvailableText : 'Currently unavailable';
      if (stockAvailableWrap && stockUnavailableWrap) {
        stockAvailableWrap.hidden = !available;
        stockUnavailableWrap.hidden = available;
      } else {
        if (!stockTextNode) {
          stockTextNode = document.createTextNode('');
          stock.appendChild(stockTextNode);
        }
        stockTextNode.nodeValue = ' ' + label;
      }
      stock.classList.toggle('is-unavailable', !available);
      stock.setAttribute('data-gf-available', available ? 'true' : 'false');
    }

    function addBusinessDays(startDate, businessDays) {
      var date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 12);
      var remaining = businessDays;
      while (remaining > 0) {
        date.setDate(date.getDate() + 1);
        var weekday = date.getDay();
        if (weekday !== 0 && weekday !== 6) remaining -= 1;
      }
      return date;
    }

    function formatArrivalDate(date) {
      var months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      var day = date.getDate();
      var remainder100 = day % 100;
      var suffix = 'th';
      if (remainder100 < 11 || remainder100 > 13) {
        if (day % 10 === 1) suffix = 'st';
        else if (day % 10 === 2) suffix = 'nd';
        else if (day % 10 === 3) suffix = 'rd';
      }
      return months[date.getMonth()] + ' ' + day + suffix;
    }

    function syncTierTray(opt) {
      var selectedWrap = opt && opt.closest('[data-gf-tier-wrap]');
      tierWraps.forEach(function (wrap) {
        wrap.classList.toggle('is-selected', wrap === selectedWrap);
      });
      if (!tierTray || !selectedWrap) return;
      if (tierTray.parentElement !== selectedWrap) selectedWrap.appendChild(tierTray);
      tierTray.hidden = false;
    }

    function syncGifts() {
      // Keep the selected offer panel aligned with the purchase mode.
      root.querySelectorAll('[data-gf-sub-only]').forEach(function (el) {
        el.hidden = mode !== 'subscription';
      });
      root.querySelectorAll('[data-gf-otp-only]').forEach(function (el) {
        el.hidden = mode !== 'onetime';
      });
      var opt = selected();
      var giftNumbers = (opt.dataset.giftNumbers || '').split(',').map(function (value) {
        return value.trim();
      }).filter(Boolean);
      root.querySelectorAll('[data-gf-gift-card]').forEach(function (card) {
        card.hidden = giftNumbers.indexOf(card.dataset.giftNumber) === -1;
      });
      root.setAttribute('data-gf-mode', mode);
    }

    function render() {
      var opt = selected();
      if (mode === 'subscription' && opt.dataset.subscriptionAvailable !== 'true') mode = 'onetime';
      syncTierTray(opt);
      var price = currentPrice(opt);
      if (ctaPrice) ctaPrice.textContent = price;
      syncComparePrices(opt);
      if (onetimePrice) onetimePrice.textContent = opt.dataset.priceOnetime;
      if (refillToggle) refillToggle.checked = mode === 'subscription';
      if (refillCard) refillCard.classList.toggle('is-checked', mode === 'subscription');
      if (typeof opt.dataset.refillSavings !== 'undefined') {
        root.querySelectorAll('[data-gf-refill-savings]').forEach(function (value) {
          value.textContent = opt.dataset.refillSavings;
        });
      }
      if (typeof opt.dataset.refillFirstDiscount !== 'undefined') {
        root.querySelectorAll('[data-gf-refill-first-discount]').forEach(function (value) {
          value.textContent = opt.dataset.refillFirstDiscount;
        });
      }
      if (typeof opt.dataset.refillRecurringDiscount !== 'undefined') {
        root.querySelectorAll('[data-gf-refill-recurring-discount]').forEach(function (value) {
          value.textContent = opt.dataset.refillRecurringDiscount;
        });
      }
      if (typeof opt.dataset.refillDays !== 'undefined') {
        root.querySelectorAll('[data-gf-refill-days]').forEach(function (value) {
          value.textContent = opt.dataset.refillDays;
        });
      }
      syncStock(opt);
      // Mirror to the sticky CTA if present.
      document.querySelectorAll('[data-gf-sticky-price]').forEach(function (n) { n.textContent = price; });
      root.querySelectorAll('[data-gf-mode-line]').forEach(function (line) {
        var showIn = line.dataset.gfModeLine === 'onetime' ? 'subscription' : 'onetime';
        var unavailableSubscription = line.dataset.gfModeLine === 'subscription' && opt.dataset.subscriptionAvailable !== 'true';
        line.hidden = mode !== showIn || unavailableSubscription;
      });
      var discount = Number(opt.dataset.subscriptionDiscount) || 0;
      var subscribeLabel = root.querySelector('[data-gf-subscribe-link]');
      if (subscribeLabel) {
        subscribeLabel.textContent = discount > 0
          ? (root.dataset.gfSubscribeLabel || 'Subscribe & save') + ' ' + discount + '%'
          : (root.dataset.gfSubscribeNoDiscountLabel || 'Subscribe');
      }
      syncGifts();
      var error = availabilityError(opt);
      if (cta.dataset.gfBusy !== '1') {
        cta.disabled = Boolean(error);
        if (error) cta.setAttribute('title', error);
        else cta.removeAttribute('title');
      }
      // Expose the live add-line for the sticky CTA / integrations.
      root.__gfLine = buildLine(opt);
    }

    function showVariantImage(option) {
      var src = option.dataset.variantImage;
      if (!src) return;
      // The variant image is product media, so it's already a slide —
      // scroll the track to it (thumb state follows from the scroll).
      if (typeof root.__gfGalleryGoToSrc === 'function') root.__gfGalleryGoToSrc(src);
    }

    function selectOption(option, viaUser) {
      options.forEach(function (o) {
        o.classList.remove('is-selected');
        o.setAttribute('aria-checked', 'false');
        o.setAttribute('tabindex', '-1');
      });
      option.classList.add('is-selected');
      option.setAttribute('aria-checked', 'true');
      option.setAttribute('tabindex', '0');
      // Only swap the gallery on a real click — the initial preselect must
      // leave the carousel on media #1, fully scrolled left (iPhone bug).
      if (viaUser) showVariantImage(option);
      render();
      if (viaUser) {
        gfTrack('gf_supply_selected', { supply_tier: option.dataset.tier, variant_id: option.dataset.variantId });
        var selectedWrap = option.closest('[data-gf-tier-wrap]');
        if (mode === 'subscription' && selectedWrap && !selectedWrap.querySelector('[data-gf-sub-only]:not([hidden])')) {
          // gift tray became visible for this selection
        }
        gfTrack('gf_subscription_gifts_viewed', { supply_tier: option.dataset.tier });
      }
    }

    function buildLine(opt) {
      var variantId = opt.dataset.variantId;
      if (!variantId || opt.dataset.variantAvailable !== 'true') return null;
      if (mode === 'subscription' && (opt.dataset.subscriptionAvailable !== 'true' || !opt.dataset.sellingPlan)) return null;
      var item = { id: Number(variantId), quantity: 1, properties: {} };
      if (isRosabella) {
        item.properties._gf_offer_id = offerId;
        item.properties._gf_offer_role = 'paid';
      }
      var supplyMonths = Number(opt.dataset.supplyMonths || 0);
      // Keep the initial-supply promise on the Rosabella PDP, not as checkout
      // line-item clutter. Other V3 templates retain their existing property.
      if (!isRosabella && supplyMonths > 0) {
        item.properties.Supply = supplyMonths + (supplyMonths === 1 ? ' Month' : ' Months');
      }
      var bogoPaidCount = Number(opt.dataset.bogoPaidCount || 0);
      var bogoFreeCount = Number(opt.dataset.bogoFreeCount || 0);
      if (bogoPaidCount > 0 && bogoFreeCount > 0) {
        item.properties.Offer = 'Buy ' + bogoPaidCount + ' + Get ' + bogoFreeCount + ' Free';
        item.properties._gf_bogo_paid_units = String(bogoPaidCount);
        item.properties._gf_bogo_free_units = String(bogoFreeCount);
      }
      var bonusVariantId = Number(opt.dataset.bonusVariantId || 0);
      var bonusQuantity = Number(opt.dataset.bonusQuantity || 0);
      if (
        isRosabella &&
        isFinite(bonusVariantId) &&
        bonusVariantId > 0 &&
        Math.floor(bonusVariantId) === bonusVariantId &&
        isFinite(bonusQuantity) &&
        bonusQuantity > 0 &&
        Math.floor(bonusQuantity) === bonusQuantity
      ) {
        if (postCheckoutBogo) {
          // Shopify Flow adds this first-delivery bonus after checkout. These
          // private attributes are intentionally inert in the storefront cart:
          // they describe the entitlement without creating a second gummy line.
          item.properties._gf_first_order_bonus_program = 'bogo-pouch-v1';
          item.properties._gf_first_order_bonus_variant_id = String(bonusVariantId);
          item.properties._gf_first_order_bonus_quantity = String(bonusQuantity);
        } else {
          // Fail-safe launch state: keep the bonus in checkout until the Flow
          // and its 3PL release gate are both active and tested.
          item.properties._gf_bonus = bonusVariantId + '~' + bonusQuantity;
        }
      }
      if (mode === 'subscription' && opt.dataset.sellingPlan) {
        item.selling_plan = Number(opt.dataset.sellingPlan);
        if (opt.dataset.recurringPackout) {
          // Keep fulfillment metadata on the single paid recurring line.
          // The launch gate must confirm Shopify Subscriptions preserves these
          // custom attributes on a generated renewal before this drives 3PL
          // automation.
          item.properties._gf_recurring_packout = opt.dataset.recurringPackout;
          item.properties._gf_recurring_structure = 'single-paid-line-v1';
        }
        if (opt.dataset.recurringPackoutProgram) {
          item.properties._gf_packout_program = opt.dataset.recurringPackoutProgram;
        }
        var packoutVariantId = Number(opt.dataset.recurringPackoutVariantId || 0);
        var packoutQuantity = Number(opt.dataset.recurringPackoutQuantity || 0);
        if (
          isFinite(packoutVariantId) &&
          packoutVariantId > 0 &&
          isFinite(packoutQuantity) &&
          packoutQuantity > 0
        ) {
          item.properties._gf_packout_variant_id = String(packoutVariantId);
          item.properties._gf_packout_quantity = String(Math.floor(packoutQuantity));
        }
        if (Number(opt.dataset.refillDays || 0) > 0) {
          item.properties._gf_packout_every_days = String(Number(opt.dataset.refillDays));
        }
        // Gift program markers (durable enforcement is server-side; see brief).
        item.properties._gf_gift_program = 'active-subscription-v1';
        // The pack line carries its own bundle manifest — "id~plan~note"
        // entries, |-separated — so the cart-side reconciler in
        // gutful-chrome.js can enforce the bundle from the line alone.
        var mIds = (opt.dataset.giftVariants || root.dataset.gfGiftVariants || '').split(',').filter(Boolean);
        if (mIds.length) {
          var mNotes = {};
          (root.dataset.gfGiftNotes || '').split('||').filter(Boolean).forEach(function (pair) {
            var ci = pair.indexOf('::');
            if (ci > 0) mNotes[pair.slice(0, ci)] = pair.slice(ci + 2);
          });
          var mSubs = isRosabella
            ? []
            : (root.dataset.gfGiftSubs || '').split(',').filter(Boolean);
          item.properties._gf_gifts = mIds.map(function (id) {
            var planToken = '';
            if (mSubs.indexOf(id) !== -1) planToken = '1';
            return id + '~' + planToken + '~' + (mNotes[id] || '');
          }).join('|');
        }
      }
      return item;
    }

    options.forEach(function (option) {
      option.addEventListener('click', function () { selectOption(option, true); });
      option.addEventListener('keydown', function (e) {
        var keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); selectOption(option, true); return; }
        if (keys.indexOf(e.key) === -1) return;
        e.preventDefault();
        var current = Array.prototype.indexOf.call(options, option);
        var next = current;
        if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = options.length - 1;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (current - 1 + options.length) % options.length;
        else next = (current + 1) % options.length;
        selectOption(options[next], true);
        options[next].focus();
      });
    });

    root.querySelectorAll('[data-gf-mode-toggle]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var nextMode = link.dataset.gfModeToggle === 'onetime' ? 'onetime' : 'subscription';
        if (nextMode === 'subscription' && selected().dataset.subscriptionAvailable !== 'true') {
          setStatus(root, 'Subscription is not available for this supply option.');
          return;
        }
        mode = nextMode;
        render();
        gfTrack('gf_purchase_mode_changed', { purchase_mode: mode });
      });
    });

    if (refillToggle) {
      refillToggle.addEventListener('change', function () {
        var nextMode = refillToggle.checked ? 'subscription' : 'onetime';
        if (nextMode === 'subscription' && selected().dataset.subscriptionAvailable !== 'true') {
          mode = 'onetime';
          refillToggle.checked = false;
          setStatus(root, 'Automatic refills are not configured for this supply option.');
          render();
          return;
        }
        mode = nextMode;
        render();
        gfTrack('gf_purchase_mode_changed', { purchase_mode: mode });
      });
    }

    // Tier bonus pouches ride with both purchase modes so "Get 1/2 Free"
    // remains true in one-time checkout. The other $0 value-stack gifts are
    // subscription-only and skip any variant already in the cart.
    function requiredGiftError(reason, cause) {
      var error = new Error(reason);
      error.gfRequiredGift = true;
      error.gfReason = reason;
      if (cause) error.gfCause = cause;
      return error;
    }

    function cartLinePlanId(line) {
      return line && line.selling_plan_allocation
        ? Number(line.selling_plan_allocation.selling_plan.id)
        : null;
    }

    function cartLineHasOfferRole(line, role) {
      var properties = (line && line.properties) || {};
      return String(properties._gf_offer_id || '') === offerId &&
        String(properties._gf_offer_role || '') === role;
    }

    function cartLineMatchesOfferRequirement(line, variantId, sellingPlanId, role) {
      if (!line || Number(line.variant_id || line.id) !== Number(variantId)) return false;
      if (cartLinePlanId(line) !== (sellingPlanId ? Number(sellingPlanId) : null)) return false;
      if (!isRosabella) return true;
      if (!cartLineHasOfferRole(line, role)) return false;
      var properties = line.properties || {};
      if (role === 'gift') return String(properties._gf_free_gift || '') === '1';
      if (role === 'bonus') {
        return properties._gf_first_order_bonus === true ||
          String(properties._gf_first_order_bonus || '') === 'true';
      }
      return role === 'paid';
    }

    function giftLines() {
      // Gifts are per tier: the selected option carries its own list
      // (data-gift-variants); the section-level list is the fallback.
      var sel = root.querySelector('[data-gf-supply-option].is-selected, .gf-supply-option.is-selected');
      var src = isRosabella && sel && typeof sel.dataset.giftVariants !== 'undefined'
        ? sel.dataset.giftVariants
        : ((sel && sel.dataset.giftVariants) || root.dataset.gfGiftVariants || '');
      var subscriptionMode = mode === 'subscription';
      var rawIds = subscriptionMode
        ? src.split(',').map(function (id) { return id.trim(); }).filter(Boolean)
        : [];
      var bonusIdRaw = isRosabella && sel ? (sel.dataset.bonusVariantId || '').trim() : '';
      var bonusQuantityRaw = isRosabella && sel ? (sel.dataset.bonusQuantity || '').trim() : '';
      var bonusId = Number(bonusIdRaw);
      var bonusQuantity = Number(bonusQuantityRaw);
      var bonusDeclared = isRosabella && (
        (bonusIdRaw !== '' && bonusIdRaw !== '0') ||
        (bonusQuantityRaw !== '' && bonusQuantityRaw !== '0')
      );
      if (
        bonusDeclared &&
        (
          !isFinite(bonusId) ||
          bonusId <= 0 ||
          Math.floor(bonusId) !== bonusId ||
          !isFinite(bonusQuantity) ||
          bonusQuantity <= 0 ||
          Math.floor(bonusQuantity) !== bonusQuantity
        )
      ) {
        return Promise.reject(requiredGiftError('bonus_variant_resolution_failed'));
      }
      var expectedRaw = sel && sel.dataset.giftExpectedLines;
      var expectedLines = typeof expectedRaw === 'undefined' || expectedRaw === '' ? null : Number(expectedRaw);
      var resolvedExpectedLines = rawIds.length + (bonusDeclared ? 1 : 0);
      if (
        subscriptionMode &&
        isRosabella &&
        expectedLines !== null &&
        isFinite(expectedLines) &&
        resolvedExpectedLines < expectedLines
      ) {
        return Promise.reject(requiredGiftError('gift_variant_resolution_failed'));
      }
      if (!rawIds.length && !bonusDeclared) return Promise.resolve([]);
      var ids = rawIds.map(Number);
      if (ids.some(function (id) { return !isFinite(id) || id <= 0; })) {
        if (isRosabella) return Promise.reject(requiredGiftError('gift_variant_resolution_failed'));
        ids = ids.filter(function (id) { return isFinite(id) && id > 0; });
      }
      if (!ids.length && !bonusDeclared) return Promise.resolve([]);
      // Per-gift cart/checkout notes ("123::Sends via email||...") set in
      // the customizer ride along as a visible line property.
      var notes = {};
      (root.dataset.gfGiftNotes || '').split('||').filter(Boolean).forEach(function (pair) {
        var i = pair.indexOf('::');
        if (i > 0) notes[Number(pair.slice(0, i))] = pair.slice(i + 2);
      });
      // Gifts flagged "renews with the subscription" ride the same selling
      // plan as the main line, so the cart shows their delivery cadence.
      var subIds = isRosabella
        ? []
        : (root.dataset.gfGiftSubs || '').split(',').filter(Boolean).map(Number);
      var planId = sel && sel.dataset.sellingPlan ? Number(sel.dataset.sellingPlan) : null;
      return fetch(gfRoute('cart.js'))
        .then(function (r) { if (!r.ok) throw new Error('cart unavailable'); return r.json(); })
        .then(function (cart) {
          var lines = ids
            .filter(function (id) {
              var requiredPlan = null;
              if (planId && subIds.indexOf(id) !== -1) {
                requiredPlan = planId;
              }
              return !cart.items.some(function (line) {
                if (isRosabella) {
                  return cartLineMatchesOfferRequirement(line, id, requiredPlan, 'gift');
                }
                return Number(line.variant_id) === id;
              });
            })
            .map(function (id) {
              var props = { _gf_free_gift: '1', _gf_gift_program: 'active-subscription-v1' };
              if (isRosabella) {
                props._gf_offer_id = offerId;
                props._gf_offer_role = 'gift';
              }
              if (notes[id]) {
                // "Key: Value" notes choose their own property label
                // (e.g. "Supply: Lifetime"); plain notes show as Delivery.
                var note = notes[id], ci = note.indexOf(': ');
                if (ci > 0) props[note.slice(0, ci)] = note.slice(ci + 2);
                else props.Delivery = note;
              }
              var line = { id: id, quantity: 1, properties: props };
              if (planId && subIds.indexOf(id) !== -1) {
                line.selling_plan = planId;
              }
              return line;
            });
          if (bonusDeclared && !postCheckoutBogo) {
            var existingBonusQuantity = cart.items.reduce(function (quantity, line) {
              var matches = isRosabella
                ? cartLineMatchesOfferRequirement(line, bonusId, null, 'bonus')
                : Number(line.variant_id) === bonusId;
              return matches ? quantity + (Number(line.quantity) || 0) : quantity;
            }, 0);
            var bonusQuantityToAdd = Math.max(0, bonusQuantity - existingBonusQuantity);
            if (bonusQuantityToAdd > 0) {
              var bonusProperties = {
                Gift: 'First-order bonus pouch',
                _gf_first_order_bonus: 'true'
              };
              if (isRosabella) {
                bonusProperties._gf_offer_id = offerId;
                bonusProperties._gf_offer_role = 'bonus';
              }
              lines.push({
                id: bonusId,
                quantity: bonusQuantityToAdd,
                properties: bonusProperties
              });
            }
          }
          return lines;
        })
        .catch(function (error) {
          if (isRosabella) throw requiredGiftError('gift_cart_resolution_failed', error);
          return [];
        });
    }

    // Keep Horizon's native one-request animation path. Legacy V3 retries the
    // paid item if a gift fails; Rosabella fails closed so its advertised
    // gift bundle can never silently degrade before checkout.
    function reportSkippedGifts(gifts) {
      gifts.forEach(function (gift) {
        document.dispatchEvent(new CustomEvent('gutful:gift:unavailable', { detail: { variantId: gift.id } }));
        gfTrack('gf_gift_skipped', { variant_id: gift.id, reason: 'unavailable_or_incompatible' });
      });
    }

    function isPreviewCartSessionRejection(json) {
      if (!gfUseConfiguredCommerceOnPreview(window.location)) return false;
      var status = Number(json && json.status);
      var description = String(
        json && (json.description || json.message || json.error) || ''
      );
      return status === 422 && /sold out|not available|unavailable/i.test(description);
    }

    function postCartLines(lines, allowPreviewSessionRetry) {
      return fetch(gfRoute('cart/add.js') + '?__gfsweep=1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: lines, sections: 'cart-drawer-section' })
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return {}; }).then(function (json) {
              if (
                allowPreviewSessionRetry !== false &&
                isPreviewCartSessionRejection(json)
              ) {
                previewCartSessionPromise = null;
                return ensurePreviewCartSession().then(function () {
                  return postCartLines(lines, false);
                });
              }
              throw json;
            });
          }
          return res.json();
        });
    }

    function rosabellaOfferVariantIds() {
      var ids = [];
      function add(raw) {
        var id = Number(raw);
        if (isFinite(id) && id > 0 && ids.indexOf(id) === -1) ids.push(id);
      }
      options.forEach(function (option) {
        add(option.dataset.variantId);
        add(option.dataset.bonusVariantId);
        (option.dataset.giftVariants || '').split(',').forEach(add);
      });
      (root.dataset.gfGiftVariants || '').split(',').forEach(add);
      return ids;
    }

    function rosabellaPaidVariantIds() {
      var ids = [];
      options.forEach(function (option) {
        var id = Number(option.dataset.variantId);
        if (isFinite(id) && id > 0 && ids.indexOf(id) === -1) ids.push(id);
      });
      return ids;
    }

    function isLegacyRosabellaOfferLine(line, offerIds, paidIds) {
      var properties = (line && line.properties) || {};
      // A tagged line belonging to another offer must never be swept merely
      // because it happens to use the same Shopify variant.
      if (properties._gf_offer_id) return false;
      var variantId = Number(line && line.variant_id);
      if (offerIds.indexOf(variantId) === -1) return false;
      if (properties._gf_free_gift || properties._gf_first_order_bonus) return true;
      if (paidIds.indexOf(variantId) === -1) return false;
      // Backward compatibility for carts created before _gf_offer_id shipped.
      // All prior Rosabella paid lines carried at least one of these fields.
      return Boolean(
        properties._gf_gifts ||
        properties._gf_gift_program ||
        properties._gf_bogo_paid_units ||
        properties._gf_bogo_free_units ||
        properties.Offer ||
        properties.Supply
      );
    }

    // The Rosabella page is a direct-to-checkout offer. Replace only its
    // owned paid/gift/bonus lines so switching tiers cannot leave a stale
    // bundle behind. Variant IDs are used only as a marker-aware fallback for
    // carts created before the ownership properties existed.
    function clearRosabellaOfferCart() {
      if (!isRosabella) return Promise.resolve();
      var offerIds = rosabellaOfferVariantIds();
      var paidIds = rosabellaPaidVariantIds();
      if (!offerIds.length) return Promise.resolve();
      return fetch(gfRoute('cart.js'))
        .then(function (res) {
          if (!res.ok) throw new Error('cart unavailable');
          return res.json();
        })
        .then(function (cart) {
          var updates = {};
          cart.items.forEach(function (line) {
            var properties = line.properties || {};
            var owned = String(properties._gf_offer_id || '') === offerId;
            if (owned || isLegacyRosabellaOfferLine(line, offerIds, paidIds)) updates[line.key] = 0;
          });
          if (!Object.keys(updates).length) return cart;
          return fetch(gfRoute('cart/update.js') + '?__gfsweep=1', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates })
          }).then(function (res) {
            if (!res.ok) throw new Error('offer cart cleanup failed');
            return res.json();
          });
        });
    }

    function verifyRosabellaBundle(item, gifts) {
      if (!isRosabella) return Promise.resolve();
      return fetch(gfRoute('cart.js'))
        .then(function (res) {
          if (!res.ok) throw new Error('cart unavailable');
          return res.json();
        })
        .then(function (cart) {
          var paidLine = cart.items.some(function (line) {
            return cartLineMatchesOfferRequirement(line, item.id, item.selling_plan || null, 'paid');
          });
          if (!paidLine) throw requiredGiftError('paid_offer_line_missing');

          gifts.forEach(function (gift) {
            var expectedProperties = gift.properties || {};
            var expectedRole = expectedProperties._gf_first_order_bonus ? 'bonus' : 'gift';
            var qualifyingLines = cart.items.filter(function (line) {
              return cartLineMatchesOfferRequirement(
                line,
                gift.id,
                gift.selling_plan || null,
                expectedRole
              );
            });
            var quantity = qualifyingLines.reduce(function (sum, line) {
              return sum + (Number(line.quantity) || 0);
            }, 0);
            var charged = qualifyingLines.some(function (line) {
              return Number(line.final_line_price) > 0;
            });
            if (quantity < (Number(gift.quantity) || 1)) {
              throw requiredGiftError('gift_bundle_incomplete');
            }
            if (charged) throw requiredGiftError('gift_discount_missing');
          });
        });
    }

    function withGiftReconcileSuspended(work) {
      if (!isRosabella) return Promise.resolve().then(work);
      var reconciler = window.__gfGiftReconcile;
      var token = reconciler && typeof reconciler.suspend === 'function'
        ? reconciler.suspend('rosabella-direct-replacement')
        : null;
      if (!token) {
        document.dispatchEvent(new CustomEvent('gutful:gift-reconcile:suspend'));
      }
      var idle = token && reconciler && typeof reconciler.whenIdle === 'function'
        ? reconciler.whenIdle()
        : Promise.resolve();
      function release() {
        if (token && reconciler && typeof reconciler.resume === 'function') {
          reconciler.resume(token);
        } else {
          document.dispatchEvent(new CustomEvent('gutful:gift-reconcile:resume'));
        }
      }
      return Promise.resolve(idle)
        .then(work)
        .then(function (value) {
          release();
          return value;
        }, function (error) {
          release();
          throw error;
        });
    }

    // Primary CTA + sticky CTA both call this.
    function addToCart(sourceButton) {
      var opt = selected();
      var item = buildLine(opt);
      gfTrack('gf_atc_clicked', { supply_tier: opt.dataset.tier, purchase_mode: mode, source: sourceButton === cta ? 'primary' : 'sticky' });

      if (!item) {
        var configError = availabilityError(opt) || 'This supply option is unavailable.';
        flash(sourceButton, 'UNAVAILABLE');
        setStatus(root, configError);
        gfTrack('gf_add_to_cart_error', { variant_id: opt.dataset.variantId || null, purchase_mode: mode, reason: 'invalid_configuration' });
        return;
      }
      if (sourceButton.dataset.gfBusy === '1') return; // double-click guard
      sourceButton.dataset.gfBusy = '1';
      sourceButton.disabled = true;
      sourceButton.setAttribute('aria-busy', 'true');
      setStatus(root, 'Adding to cart…');

      withGiftReconcileSuspended(function () {
        return ensurePreviewCartSession()
          .then(function () {
            return isRosabella ? clearRosabellaOfferCart() : Promise.resolve();
          })
          .then(giftLines)
          .then(function (gifts) {
            var bundle = gifts.slice().reverse().concat([item]);
            return postCartLines(bundle)
              .then(function (json) {
                if (isRosabella && gifts.length) {
                  var addedLines = Array.isArray(json.items) ? json.items : [];
                  var missingGift = gifts.some(function (gift) {
                    var expectedProperties = gift.properties || {};
                    var expectedRole = expectedProperties._gf_first_order_bonus ? 'bonus' : 'gift';
                    var addedQuantity = addedLines.reduce(function (quantity, line) {
                      if (!cartLineMatchesOfferRequirement(
                        line,
                        gift.id,
                        gift.selling_plan || null,
                        expectedRole
                      )) return quantity;
                      return quantity + (Number(line.quantity) || 0);
                    }, 0);
                    return addedQuantity < (Number(gift.quantity) || 1);
                  });
                  if (missingGift) throw requiredGiftError('gift_bundle_incomplete');
                }
                return verifyRosabellaBundle(item, gifts).then(function () {
                  return { json: json, gifts: gifts };
                });
              })
              .catch(function (error) {
                if (isRosabella) {
                  return clearRosabellaOfferCart()
                    .catch(function () { /* preserve the original offer error */ })
                    .then(function () {
                      if (error && error.gfRequiredGift) throw error;
                      throw requiredGiftError('gift_bundle_add_failed', error);
                    });
                }
                if (!gifts.length || (error && error.gfRequiredGift)) throw error;
                reportSkippedGifts(gifts);
                return postCartLines([item]).then(function (json) {
                  return { json: json, gifts: [] };
                });
              });
          });
      })
        .then(function (result) {
          return result && result.json ? result.json : result;
        })
        .then(function (json) {
          gfTrack('gf_add_to_cart_success', { variant_id: item.id, selling_plan_id: item.selling_plan || null, purchase_mode: mode });
          document.dispatchEvent(new CustomEvent('gutful:cart:added', { detail: item }));
          // Customizer option: bypass the drawer and go straight to checkout.
          if (root.hasAttribute('data-gf-skip-checkout')) {
            setStatus(root, 'Redirecting to checkout…');
            window.location.href = gfRoute('checkout');
            return;
          }
          sourceButton.dataset.gfBusy = '';
          sourceButton.disabled = false;
          sourceButton.removeAttribute('aria-busy');
          var animationImage = opt.dataset.variantImage
            || (typeof root.__gfGalleryCurrentImage === 'function' && root.__gfGalleryCurrentImage())
            || (mainImage && (mainImage.currentSrc || mainImage.src));
          flyToCart(animationImage, sourceButton);
          refreshCartCount();
          notifyCartComponents(sourceButton, json.sections);
          setTimeout(openCartDrawer, reduceMotion ? 0 : 420);
          flash(sourceButton, 'ADDED TO CART ✓');
          setStatus(root, 'Added to cart.');
        })
        .catch(function (err) {
          sourceButton.dataset.gfBusy = '';
          sourceButton.disabled = false;
          sourceButton.removeAttribute('aria-busy');
          var msg = (err && err.description) ? err.description : 'COULD NOT ADD - TRY AGAIN';
          flash(sourceButton, msg.length > 40 ? 'COULD NOT ADD - TRY AGAIN' : msg);
          setStatus(
            root,
            err && err.gfRequiredGift
              ? 'Your included gifts could not be added. Please try again before checking out.'
              : 'Could not add to cart. Please try again.'
          );
          gfTrack('gf_add_to_cart_error', {
            variant_id: item.id,
            purchase_mode: mode,
            reason: err && err.gfReason ? err.gfReason : 'cart_add_failed'
          });
          if (window.console && typeof window.console.error === 'function') {
            window.console.error('[Gutful cart]', {
              reason: err && err.gfReason ? err.gfReason : 'cart_add_failed',
              cause: err && (err.gfCause || err.description || err.message || err)
            });
          }
        });
    }

    cta.addEventListener('click', function () { addToCart(cta); });
    root.__gfAddToCart = addToCart; // exposed for sticky CTA
    root.__gfCta = cta;

    function flash(button, text) {
      var original = button.dataset.gfLabel || button.innerHTML;
      button.dataset.gfLabel = original;
      button.textContent = text;
      button.disabled = true;
      setTimeout(function () {
        button.innerHTML = original;
        button.disabled = false;
        button.dataset.gfLabel = '';
        render();
      }, 1600);
    }

    options.forEach(seedConfiguredCommerce);
    var initialOption = selected();
    var defaultTier = String(root.dataset.gfDefaultTier || '').trim();
    var requestedPurchaseMode = '';
    if (defaultTier) {
      Array.prototype.some.call(options, function (option) {
        if (option.dataset.tier !== defaultTier) return false;
        initialOption = option;
        return true;
      });
    }
    try {
      var params = new URLSearchParams(window.location.search);
      var requestedVariant = params.get('variant');
      if (requestedVariant && /^\d+$/.test(requestedVariant)) {
        Array.prototype.some.call(options, function (option) {
          if (option.dataset.variantId !== requestedVariant) return false;
          initialOption = option;
          return true;
        });
      }
      var requestedPurchase = params.get('purchase');
      if (requestedPurchase === 'subscription') requestedPurchaseMode = 'subscription';
      else if (requestedPurchase === 'one-time') requestedPurchaseMode = 'onetime';
    } catch (e) { /* malformed URLs fall back to server-rendered defaults */ }
    if (requestedPurchaseMode) {
      mode = requestedPurchaseMode;
    } else if (
      root.dataset.gfDefaultMode === 'subscription' &&
      initialOption.dataset.subscriptionAvailable === 'true'
    ) {
      mode = 'subscription';
    } else if (root.dataset.gfDefaultMode === 'onetime') {
      mode = 'onetime';
    }
    selectOption(initialOption, false);
    root.__gfCommerceReady = Promise.all(
      Array.prototype.map.call(options, hydrateCommerceOption)
    ).then(function () {
      render();
      return Array.prototype.map.call(options, function (option) {
        return {
          variantId: option.dataset.variantId || '',
          sellingPlanId: option.dataset.sellingPlan || '',
          available: option.dataset.variantAvailable === 'true',
          subscriptionAvailable: option.dataset.subscriptionAvailable === 'true',
          state: option.dataset.gfCommerceState || ''
        };
      });
    });
  }

  function setStatus(root, msg) {
    var live = root.querySelector('[data-gf-live]');
    if (live) live.textContent = msg;
  }

  /* ---------------------------------------------------------------
     Fly-to-cart — preserved from V1. Prefers Horizon's <fly-to-cart>
     custom element (styles/keyframes live in the theme); WAAPI ghost is
     the fallback for themes without it. Honors reduced motion.
     --------------------------------------------------------------- */
  function flyToCart(image, sourceEl) {
    if (reduceMotion) return;
    var dest = document.querySelector('[data-gf-cart-icon]') || document.querySelector('.header-actions__cart-icon');
    if (!dest || !image || !sourceEl) return;

    if (window.customElements && customElements.get('fly-to-cart')) {
      var el = document.createElement('fly-to-cart');
      el.classList.add('fly-to-cart--main');
      el.style.setProperty('background-image', 'url(' + image + ')');
      el.style.setProperty('--start-opacity', '0');
      el.source = sourceEl;
      el.destination = dest;
      document.body.appendChild(el);
      return;
    }

    var s = sourceEl.getBoundingClientRect();
    var d = dest.getBoundingClientRect();
    var ghost = document.createElement('img');
    ghost.src = image;
    ghost.style.cssText = 'position:fixed;z-index:2147483647;pointer-events:none;width:56px;height:56px;object-fit:cover;border-radius:12px;' +
      'left:' + (s.left + s.width / 2 - 28) + 'px;top:' + (s.top + s.height / 2 - 28) + 'px;';
    document.body.appendChild(ghost);
    ghost.animate(
      [
        { transform: 'translate(0,0) scale(1)', opacity: 1 },
        { transform: 'translate(' + (d.left + d.width / 2 - (s.left + s.width / 2)) + 'px,' + (d.top + d.height / 2 - (s.top + s.height / 2)) + 'px) scale(0.2)', opacity: 0.3 }
      ],
      { duration: 650, easing: 'cubic-bezier(0.5, -0.3, 0.8, 0.6)' }
    ).finished.then(function () { ghost.remove(); }).catch(function () { ghost.remove(); });
  }

  /* ---- Horizon cart-drawer adapter (preserved from V1) ---- */
  function refreshCartCount() {
    fetch(gfRoute('cart.js')).then(function (r) { return r.json(); }).then(function (cart) {
      document.querySelectorAll('[data-gf-cart-count]').forEach(function (b) {
        b.textContent = cart.item_count;
        b.hidden = cart.item_count === 0;
      });
    }).catch(function () {});
  }
  function getCartDrawer() { return document.getElementById('cart-drawer'); }
  function openCartDrawer() {
    var drawer = getCartDrawer();
    if (drawer && typeof drawer.open === 'function') { drawer.open(); return true; }
    var dialog = drawer && drawer.querySelector('dialog');
    if (dialog && typeof dialog.showModal === 'function') { dialog.showModal(); return true; }
    return false;
  }
  function notifyCartComponents(sourceEl, sections) {
    if (!sections) return;
    try {
      var ev = new Event('shopify:cart:lines-update', { bubbles: true, composed: true });
      ev.action = 'add';
      ev.promise = Promise.resolve({ detail: { sections: sections } });
      (sourceEl || document).dispatchEvent(ev);
    } catch (e) { /* non-critical */ }
  }
  function initCartIcons(scope) {
    (scope.querySelectorAll ? scope : document).querySelectorAll('[data-gf-cart-icon]').forEach(function (icon) {
      if (icon.dataset.gfDrawerInit) return;
      icon.dataset.gfDrawerInit = '1';
      icon.addEventListener('click', function (e) {
        // Toggle: a second click on the icon closes an already-open drawer.
        var drawer = getCartDrawer();
        var dialog = drawer && drawer.querySelector('dialog');
        if (dialog && dialog.open) {
          e.preventDefault();
          if (typeof drawer.close === 'function') drawer.close();
          else dialog.close();
          return;
        }
        if (openCartDrawer()) e.preventDefault();
      });
    });
  }

  /* ---------------------------------------------------------------
     Before/after slider — preserved from V1. Two identically sized
     full-frame layers; --gf-ba-reveal moves the divider and clips the
     inflamed layer via clip-path (never resizes the image).
     --------------------------------------------------------------- */
  function initBaSlider(slider) {
    if (!slider || slider.dataset.gfBaInit) return;
    slider.dataset.gfBaInit = '1';
    var handle = slider.querySelector('[data-gf-ba-handle], .gf-ba__handle');
    var beforeBadge = slider.querySelector('.gf3-ba__badge--before, .gf-ba__badge--inflamed');
    var afterBadge = slider.querySelector('.gf3-ba__badge--after, .gf-ba__badge--healthy');
    var beforeLabel = beforeBadge && (beforeBadge.textContent || '').trim();
    var afterLabel = afterBadge && (afterBadge.textContent || '').trim();
    var engaged = false;

    function setReveal(pct, viaUser) {
      var clamped = Math.max(0, Math.min(100, pct));
      var rounded = Math.round(clamped);
      slider.style.setProperty('--gf-ba-reveal', clamped + '%');
      if (handle) {
        handle.setAttribute('aria-valuenow', rounded);
        handle.setAttribute(
          'aria-valuetext',
          beforeLabel && afterLabel
            ? rounded + '% ' + beforeLabel + ', ' + (100 - rounded) + '% ' + afterLabel
            : rounded + '% of the first state visible'
        );
      }
      if (viaUser && !engaged) { engaged = true; gfTrack('gf_lining_slider_engaged', {}); }
    }
    function pctFromEvent(e) {
      var rect = slider.getBoundingClientRect();
      var x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      return (x / rect.width) * 100;
    }
    var dragging = false;
    slider.addEventListener('pointerdown', function (e) { dragging = true; slider.setPointerCapture(e.pointerId); setReveal(pctFromEvent(e), true); });
    slider.addEventListener('pointermove', function (e) { if (dragging) setReveal(pctFromEvent(e), true); });
    slider.addEventListener('pointerup', function () { dragging = false; });
    slider.addEventListener('pointercancel', function () { dragging = false; });
    if (handle) {
      handle.addEventListener('keydown', function (e) {
        var current = parseFloat(getComputedStyle(slider).getPropertyValue('--gf-ba-reveal')) || 30;
        if (e.key === 'ArrowLeft') { setReveal(current - 4, true); e.preventDefault(); }
        if (e.key === 'ArrowRight') { setReveal(current + 4, true); e.preventDefault(); }
        if (e.key === 'Home') { setReveal(0, true); e.preventDefault(); }
        if (e.key === 'End') { setReveal(100, true); e.preventDefault(); }
      });
    }
    setReveal(parseFloat(getComputedStyle(slider).getPropertyValue('--gf-ba-reveal')) || 30, false);
  }

  /* ---------------------------------------------------------------
     Accordions — shared controller for buy-box disclosures and FAQ.
     One-open-at-a-time within a group; second click on the open row
     closes it. aria-expanded/aria-controls, height/opacity animation
     unless reduced motion.
     --------------------------------------------------------------- */
  function initAccordions(scope) {
    (scope.querySelectorAll ? scope : document).querySelectorAll('[data-gf-accordion]').forEach(function (group) {
      if (group.dataset.gfAccInit) return;
      group.dataset.gfAccInit = '1';
      var rows = group.querySelectorAll('[data-gf-acc-row]');
      var analytic = group.dataset.gfAccordion; // 'disclosure' | 'faq'
      rows.forEach(function (row) {
        var btn = row.querySelector('[data-gf-acc-trigger]');
        var panel = row.querySelector('[data-gf-acc-panel]');
        if (!btn || !panel) return;
        btn.addEventListener('click', function () {
          var isOpen = btn.getAttribute('aria-expanded') === 'true';
          rows.forEach(function (r) {
            var b = r.querySelector('[data-gf-acc-trigger]');
            var p = r.querySelector('[data-gf-acc-panel]');
            if (b && p) { b.setAttribute('aria-expanded', 'false'); p.hidden = true; r.classList.remove('is-open'); }
          });
          if (!isOpen) {
            btn.setAttribute('aria-expanded', 'true');
            panel.hidden = false;
            row.classList.add('is-open');
            gfTrack(analytic === 'faq' ? 'gf_faq_opened' : 'gf_disclosure_opened', { label: (btn.textContent || '').trim().slice(0, 60) });
          }
        });
      });
    });
  }

  /* ---------------------------------------------------------------
     Modal / bottom-sheet — study summaries. Focus trap, scroll lock,
     Escape, focus return. One controller; CSS decides modal vs sheet.
     --------------------------------------------------------------- */
  var lastFocused = null;
  function trapFocus(container, e) {
    var f = container.querySelectorAll('a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  }
  function openOverlay(overlay) {
    lastFocused = document.activeElement;
    overlay.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    var focusTarget = overlay.querySelector('[data-gf-overlay-close]') || overlay;
    requestAnimationFrame(function () { focusTarget.focus(); });
    overlay.__gfKeydown = function (e) {
      if (e.key === 'Escape') closeOverlay(overlay);
      else if (e.key === 'Tab') trapFocus(overlay, e);
    };
    overlay.addEventListener('keydown', overlay.__gfKeydown);
  }
  function closeOverlay(overlay) {
    overlay.hidden = true;
    document.documentElement.style.overflow = '';
    if (overlay.__gfKeydown) overlay.removeEventListener('keydown', overlay.__gfKeydown);
    if (lastFocused && lastFocused.focus) lastFocused.focus();
    // Story lightbox: tearing down the player stops playback.
    overlay.querySelectorAll('[data-gf-story-player]').forEach(function (p) { p.innerHTML = ''; });
  }
  function initOverlays(scope) {
    (scope.querySelectorAll ? scope : document).querySelectorAll('[data-gf-overlay-open]').forEach(function (trigger) {
      if (trigger.dataset.gfOverlayInit) return;
      trigger.dataset.gfOverlayInit = '1';
      trigger.addEventListener('click', function () {
        var overlay = document.getElementById(trigger.dataset.gfOverlayOpen);
        if (!overlay) return;
        openOverlay(overlay);
        gfTrack('gf_study_opened', { ingredient_key: trigger.dataset.ingredient || null });
      });
    });
    (scope.querySelectorAll ? scope : document).querySelectorAll('[data-gf-overlay]').forEach(function (overlay) {
      if (overlay.dataset.gfOverlayReady) return;
      overlay.dataset.gfOverlayReady = '1';
      overlay.querySelectorAll('[data-gf-overlay-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { closeOverlay(overlay); });
      });
      var backdrop = overlay.querySelector('[data-gf-overlay-backdrop]');
      if (backdrop) backdrop.addEventListener('click', function () { closeOverlay(overlay); });
      overlay.querySelectorAll('[data-gf-pubmed]').forEach(function (a) {
        a.addEventListener('click', function () { gfTrack('gf_study_pubmed_clicked', { ingredient_key: overlay.dataset.ingredient || null }); });
      });
    });
  }

  /* ---------------------------------------------------------------
     Creator story carousel — Figma 866:229 controls page the track
     (scrollBy one visible width); buttons disable at either end.
     --------------------------------------------------------------- */
  function initStoryCarousels(scope) {
    (scope.querySelectorAll ? scope : document).querySelectorAll('[data-gf-stories-carousel]').forEach(function (section) {
      if (section.dataset.gfCarouselInit) return;
      section.dataset.gfCarouselInit = '1';
      var track = section.querySelector('[data-gf-stories]');
      var prev = section.querySelector('[data-gf-stories-prev]');
      var next = section.querySelector('[data-gf-stories-next]');
      if (!track || !prev || !next) return;
      function sync() {
        var max = track.scrollWidth - track.clientWidth;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max - 2;
      }
      function page(dir) {
        track.scrollBy({ left: dir * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      prev.addEventListener('click', function () { page(-1); });
      next.addEventListener('click', function () { page(1); });
      track.addEventListener('scroll', sync, { passive: true });
      if (window.ResizeObserver) {
        section.__gfStoriesResizeObserver = new ResizeObserver(sync);
        section.__gfStoriesResizeObserver.observe(track);
      }
      sync();
    });
  }

  /* ---------------------------------------------------------------
     Creator story videos — play INSIDE the card (TikTok-style), with
     playsinline so iPhone never fullscreens. Tap toggles play/pause;
     starting a card pauses the others. No modal.
     --------------------------------------------------------------- */
  function initStories(scope) {
    (scope.querySelectorAll ? scope : document).querySelectorAll('[data-gf-story-video]').forEach(function (card) {
      if (card.dataset.gfStoryInit) return;
      card.dataset.gfStoryInit = '1';
      var media = card.querySelector('.gf-story__media');
      var toggle = card.querySelector('[data-gf-story-toggle]');
      if (!media || !toggle) return;

      function pauseOthers() {
        document.querySelectorAll('.gf-story video').forEach(function (v) {
          if (!card.contains(v)) v.pause();
        });
      }

      // The player exists from the start, but the source remains unloaded
      // until the first tap. First playback always begins at 0:00; the
      // poster covers the card while metadata loads.
      var v = null;
      if (card.dataset.gfVideoMp4) {
        v = document.createElement('video');
        v.src = card.dataset.gfVideoMp4;
        v.playsInline = true;
        v.setAttribute('playsinline', '');
        v.setAttribute('webkit-playsinline', '');
        v.loop = true;
        // ZERO network until tap — no preload, no buffer warmup. Four
        // clips are ~18MB; fetching them alongside the page drowned it
        // on slow links. Faststart mp4s still begin quickly on tap.
        v.preload = 'none';
        v.className = 'gf-story__video';
        var posterImg = media.querySelector('img');
        if (posterImg) v.poster = posterImg.currentSrc || posterImg.src;
        v.addEventListener('playing', function () { card.classList.add('is-started'); });
        v.addEventListener('play', function () { card.classList.add('is-playing'); toggle.setAttribute('aria-label', 'Pause video'); });
        v.addEventListener('pause', function () { card.classList.remove('is-playing'); toggle.setAttribute('aria-label', 'Play video'); });
        media.appendChild(v);
        // Sound toggle, top right (shown while the clip is on screen).
        var muteBtn = document.createElement('button');
        muteBtn.type = 'button';
        muteBtn.className = 'gf-story__mute';
        muteBtn.setAttribute('aria-label', 'Mute video');
        muteBtn.textContent = '🔊';
        muteBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          v.muted = !v.muted;
          muteBtn.textContent = v.muted ? '🔇' : '🔊';
          muteBtn.setAttribute('aria-label', v.muted ? 'Unmute video' : 'Mute video');
        });
        media.appendChild(muteBtn);
      }

      function startFromBeginning() {
        // Reset only the first play. Later taps resume from the paused frame.
        if (!card.classList.contains('is-started')) { try { v.currentTime = 0; } catch (e) { /* noop */ } }
        v.play();
      }

      function toggleStory() {
        if (v) {
          if (v.paused) {
            pauseOthers();
            if (v.readyState >= 1) {
              startFromBeginning();
            } else {
              v.addEventListener('loadedmetadata', startFromBeginning, { once: true });
              v.load();
            }
            gfTrack('gf_story_started', {});
          } else {
            v.pause();
          }
          return;
        }
        if (card.dataset.gfVideoEmbed) {
          if (media.querySelector('iframe')) return;
          var f = document.createElement('iframe');
          f.src = card.dataset.gfVideoEmbed;
          f.allow = 'autoplay; encrypted-media; picture-in-picture';
          f.allowFullscreen = true;
          f.className = 'gf-story__video';
          f.title = card.getAttribute('aria-label') || 'Story video';
          media.appendChild(f);
          card.classList.add('is-playing');
          card.classList.add('is-started');
          toggle.hidden = true;
          gfTrack('gf_story_started', {});
        }
      }
      toggle.addEventListener('click', toggleStory);
    });
  }

  /* ---------------------------------------------------------------
     Final CTA (scroll to selector) + mobile sticky CTA (IO-gated).
     --------------------------------------------------------------- */
  function initFinalCta(scope) {
    (scope.querySelectorAll ? scope : document).querySelectorAll('[data-gf-scroll-to]').forEach(function (btn) {
      if (btn.dataset.gfScrollInit) return;
      btn.dataset.gfScrollInit = '1';
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        var target = document.querySelector(btn.dataset.gfScrollTo);
        if (!target) return;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        var focusable =
          target.querySelector('[data-gf-supply-option][aria-checked="true"]') ||
          target.querySelector('[data-gf-supply-option].is-selected') ||
          target.querySelector('.gf-supply-option.is-selected');
        if (!focusable) {
          target.setAttribute('tabindex', '-1');
          focusable = target;
        }
        setTimeout(function () { focusable.focus({ preventScroll: true }); }, reduceMotion ? 0 : 400);
        gfTrack('gf_final_cta_clicked', {});
      });
    });
  }

  function initStickyCta() {
    var sticky = document.querySelector('[data-gf-sticky-cta]');
    var hero = document.querySelector('[data-gf-hero]');
    if (!sticky || !hero || sticky.dataset.gfStickyInit) return;
    sticky.dataset.gfStickyInit = '1';
    var primaryCta = hero.querySelector('[data-gf-atc]');
    if (!primaryCta) return;
    var seen = false;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Show only once the primary CTA has fully scrolled PAST (above the
        // viewport) — never while it is still on screen, and not at page top
        // when the CTA merely hasn't been reached yet.
        var show = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        sticky.classList.toggle('is-visible', show);
        if (show && !seen) { seen = true; gfTrack('gf_sticky_cta_viewed', {}); }
      });
    });
    io.observe(primaryCta);
    sticky.__gfIntersectionObserver = io;

    var stickyBtn = sticky.querySelector('[data-gf-sticky-atc]');
    if (stickyBtn) {
      // Rosabella can return shoppers to the selector; legacy V3 sticky
      // buttons retain their direct add-to-cart behavior.
      stickyBtn.addEventListener('click', function () {
        gfTrack('gf_sticky_cta_clicked', {});
        if (stickyBtn.dataset.gfStickyAction === 'scroll') {
          var selectorTarget = document.querySelector('[data-gf-selector]');
          if (!selectorTarget) return;
          selectorTarget.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
          var selectorFocus =
            selectorTarget.querySelector('[data-gf-supply-option][aria-checked="true"]') ||
            selectorTarget.querySelector('[data-gf-supply-option].is-selected') ||
            selectorTarget.querySelector('.gf-supply-option.is-selected');
          if (!selectorFocus) {
            selectorTarget.setAttribute('tabindex', '-1');
            selectorFocus = selectorTarget;
          }
          setTimeout(function () { selectorFocus.focus({ preventScroll: true }); }, reduceMotion ? 0 : 400);
          return;
        }
        if (typeof hero.__gfAddToCart === 'function') {
          hero.__gfAddToCart(stickyBtn);
          return;
        }
        var target = document.querySelector('[data-gf-selector]');
        if (!target) return;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        var focusable = target.querySelector('.gf-supply-option.is-selected') || target;
        setTimeout(function () { focusable.focus({ preventScroll: true }); }, reduceMotion ? 0 : 400);
      });
    }
  }

  /* ---- one-shot in-view analytics ---- */
  function initInView(scope) {
    (scope.querySelectorAll ? scope : document).querySelectorAll('[data-gf-inview]').forEach(function (el) {
      if (el.dataset.gfInviewInit) return;
      el.dataset.gfInviewInit = '1';
      var io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { gfTrack(el.dataset.gfInview, {}); obs.disconnect(); }
        });
      }, { threshold: 0.4 });
      io.observe(el);
    });
  }

  /* ---------------------------------------------------------------
     Section lifecycle — idempotent init; re-run on shopify:section:load.
     --------------------------------------------------------------- */
  function initScope(scope) {
    (scope.querySelectorAll ? scope : document).querySelectorAll('[data-gf-hero]').forEach(function (hero) {
      if (hero.dataset.gfInit) return;
      hero.dataset.gfInit = '1';
      initGallery(hero);
      initBuyBox(hero);
    });
    (scope.querySelectorAll ? scope : document).querySelectorAll('[data-gf-ba]').forEach(initBaSlider);
    initCartIcons(scope);
    initAccordions(scope);
    initOverlays(scope);
    initStories(scope);
    initStoryCarousels(scope);
    initFinalCta(scope);
    initInView(scope);
    initStickyCta();
    var pdpView = document.querySelector('[data-gf-hero]');
    if (pdpView && !window.__gfPdpViewed) { window.__gfPdpViewed = true; gfTrack('gf_pdp_view', {}); }
  }

  function boot() { initScope(document); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else if (!window.__gutfulV3Booted) boot();
  window.__gutfulV3Booted = true;

  document.addEventListener('shopify:section:load', function (e) { initScope(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var carousels = [e.target].concat(Array.prototype.slice.call(e.target.querySelectorAll('[data-gf-stories-carousel]')));
    carousels.forEach(function (carousel) {
      if (carousel.__gfStoriesResizeObserver) carousel.__gfStoriesResizeObserver.disconnect();
    });
    e.target.querySelectorAll('[data-gf-sticky-cta]').forEach(function (sticky) {
      if (sticky.__gfIntersectionObserver) sticky.__gfIntersectionObserver.disconnect();
    });
  });
})();
